/**
 * 骨架图数据解析器
 * 用于解析和验证导入的 JSON 数据
 */

import language from '@/utils/language'
import store from '@/store'

/**
 * 获取翻译文本
 * @param {string} key - 翻译键
 * @param {Object} params - 参数对象
 * @returns {string} 翻译后的文本
 */
function t(key, params = {}) {
  const lang = store.state.app.language
  let text = language[key][lang]
  // 替换参数
  Object.keys(params).forEach(param => {
    text = text.replace(`{${param}}`, params[param])
  })
  return text
}

/**
 * 解析 JSON 字符串并返回节点和边数据
 * 只支持简化格式：[{lng: Number, lat: Number}, {lng: Number, lat: Number}, ...]
 * 
 * @param {string} jsonString - JSON 字符串
 * @returns {Object} { nodes: [], edges: [], errors: [] }
 */
export function parseJSON(jsonString) {
  const result = {
    nodes: [],
    edges: [],
    errors: []
  };

  try {
    const data = JSON.parse(jsonString);

    // 只接受数组格式
    if (!Array.isArray(data)) {
      result.errors.push(t('parserFormatError'));
      return result;
    }

    // 验证数组不能为空
    if (data.length === 0) {
      result.errors.push(t('parserDataEmpty'));
      return result;
    }

    // 解析坐标数组
    data.forEach((point, index) => {
      if (!point || typeof point !== 'object') {
        result.errors.push(t('parserPointMustBeObject', { index: index + 1 }));
        return;
      }

      // 只接受 lng 和 lat 字段
      const lng = point.lng;
      const lat = point.lat;
      
      if (lng === undefined || lng === null) {
        result.errors.push(t('parserPointMissingLng', { index: index + 1 }));
        return;
      }
      
      if (lat === undefined || lat === null) {
        result.errors.push(t('parserPointMissingLat', { index: index + 1 }));
        return;
      }

      // 验证数据类型
      if (typeof lng !== 'number') {
        result.errors.push(t('parserLngMustBeNumber', { index: index + 1 }));
        return;
      }

      if (typeof lat !== 'number') {
        result.errors.push(t('parserLatMustBeNumber', { index: index + 1 }));
        return;
      }

      // 验证经纬度范围
      if (lng < -180 || lng > 180) {
        result.errors.push(t('parserLngRange', { index: index + 1 }));
        return;
      }

      if (lat < -90 || lat > 90) {
        result.errors.push(t('parserLatRange', { index: index + 1 }));
        return;
      }

      // 自动生成 ID（从 1 开始）
      // 转换为后端需要的字段名：longitude 和 latitude
      result.nodes.push({
        id: index + 1,
        longitude: lng,
        latitude: lat
      });
    });
    
    // 简化格式不包含边数据，edges 为空数组
    result.edges = [];

  } catch (e) {
    result.errors.push(t('parserJsonParseFailed') + ': ' + e.message);
  }

  return result;
}

/**
 * 验证节点数据
 * @param {Object} node - 节点对象
 * @param {number} index - 节点索引
 * @returns {Object} { valid: boolean, errors: [] }
 */
export function validateNode(node, index) {
  const errors = [];

  if (!node || typeof node !== 'object') {
    errors.push(t('parserNodeMustBeObject', { index }));
    return { valid: false, errors };
  }

  // 验证 ID
  if (node.id === undefined || node.id === null) {
    errors.push(t('parserNodeMissingId', { index }));
  } else if (typeof node.id !== 'number' && typeof node.id !== 'string') {
    errors.push(t('parserNodeIdType', { index }));
  }

  // 验证经度
  const lon = node.lon || node.longitude;
  if (lon === undefined || lon === null) {
    errors.push(t('parserNodeMissingLon', { index }));
  } else if (typeof lon !== 'number') {
    errors.push(t('parserNodeLonType', { index }));
  } else if (lon < -180 || lon > 180) {
    errors.push(t('parserNodeLonRange', { index }));
  }

  // 验证纬度
  const lat = node.lat || node.latitude;
  if (lat === undefined || lat === null) {
    errors.push(t('parserNodeMissingLat', { index }));
  } else if (typeof lat !== 'number') {
    errors.push(t('parserNodeLatType', { index }));
  } else if (lat < -90 || lat > 90) {
    errors.push(t('parserNodeLatRange', { index }));
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * 验证边数据
 * @param {Object} edge - 边对象
 * @param {Set} nodeIdSet - 节点 ID 集合
 * @param {number} index - 边索引
 * @returns {Object} { valid: boolean, errors: [] }
 */
export function validateEdge(edge, nodeIdSet, index) {
  const errors = [];

  if (!edge || typeof edge !== 'object') {
    errors.push(t('parserEdgeMustBeObject', { index }));
    return { valid: false, errors };
  }

  // 验证起始节点
  const fromNodeId = edge.from || edge.fromNodeId;
  if (fromNodeId === undefined || fromNodeId === null) {
    errors.push(t('parserEdgeMissingFrom', { index }));
  } else if (!nodeIdSet.has(fromNodeId)) {
    errors.push(t('parserEdgeFromNotExist', { index, id: fromNodeId }));
  }

  // 验证目标节点
  const toNodeId = edge.to || edge.toNodeId;
  if (toNodeId === undefined || toNodeId === null) {
    errors.push(t('parserEdgeMissingTo', { index }));
  } else if (!nodeIdSet.has(toNodeId)) {
    errors.push(t('parserEdgeToNotExist', { index, id: toNodeId }));
  }

  // 验证不能自连
  if (fromNodeId === toNodeId) {
    errors.push(t('parserEdgeSelfConnect', { index }));
  }

  // 验证权重
  if (edge.weight !== undefined && edge.weight !== null) {
    if (typeof edge.weight !== 'number') {
      errors.push(t('parserEdgeWeightType', { index }));
    } else if (edge.weight <= 0) {
      errors.push(t('parserEdgeWeightPositive', { index }));
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * 格式化 JSON 字符串
 * @param {string} jsonString - JSON 字符串
 * @returns {string} 格式化后的 JSON 字符串
 */
export function formatJSONString(jsonString) {
  try {
    const data = JSON.parse(jsonString);
    return JSON.stringify(data, null, 2);
  } catch (e) {
    throw new Error(t('parserJsonFormatError') + ': ' + e.message);
  }
}

/**
 * 验证 JSON 字符串格式
 * @param {string} jsonString - JSON 字符串
 * @returns {Object} { valid: boolean, error: string }
 */
export function validateJSONString(jsonString) {
  try {
    JSON.parse(jsonString);
    return { valid: true, error: null };
  } catch (e) {
    return { valid: false, error: e.message };
  }
}
